import React from 'react'
import {
  BankOutlined,
  CalendarOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { blue } from '../../color'
import AccessControlAction from '../../components/AccessControlAction'
import { AvatarHasName } from '../../components/Common/Avatar'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import UploadFileItem from '../../components/Common/FileItemComponent/UploadFileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PopoverWatchTime from '../../components/PopoverWatchTime'
import SelectPeoplePopup from '../../components/SelectPeoplePopup/SelectPeoplePopup'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  DATE_FORMAT_DEFAULT,
  VIEWLOG_DATE,
} from '../../constants'
import { KpiV2Request } from '../../requests/KpiV2Request'
import utils from '../../utils'
import {
  SelectList,
  SelectTitle,
} from '../Documents/InternalDocumentIncomingDocCreatePage/InternalDocumentIncomingDocCreatePageStyled'
import KpiV2SubUpsertModal from './KpiV2SubUpsertModal'

const { TextArea } = Input
const { Option } = Select

const SECTION_TITLE_STYLE = {
  color: '#2c65ac',
  fontWeight: 600,
  fontSize: 14,
  borderBottom: '1px solid #dbe3ef',
  marginBottom: 12,
  paddingBottom: 8,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}

const INFO_ROW_STYLE = {
  display: 'flex',
  borderBottom: '1px solid rgba(0,0,0,0.05)',
  padding: '8px 0',
  fontSize: 13,
}

const INFO_ROW_LABEL_STYLE = {
  width: 150,
  flexShrink: 0,
  color: '#222',
  fontWeight: 700,
}

const INFO_ROW_VALUE_STYLE = {
  flex: 1,
  color: '#222',
  wordBreak: 'break-word',
}

const CARD_STYLE = { border: '1px solid #6fa6d9' }

const BOTTOM_ACTIONS_STYLE = {
  borderTop: '1px solid #f0f0f0',
  marginTop: 16,
  paddingTop: 12,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
}

const FORM_ITEM_STYLE = { marginBottom: 12 }
const FLEX_ROW_GAP6 = { display: 'flex', gap: 6 }
const FLEX_COL_GAP4 = { display: 'flex', flexDirection: 'column', gap: 4 }
const FILE_LIST_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minHeight: 24,
}

const SUB_LIST_HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  padding: '4px 4px 8px',
  gap: 8,
  fontSize: 12,
  color: '#222',
  fontWeight: 700,
  borderBottom: '2px solid #e8edf3',
  marginBottom: 2,
}

const toTitleCase = str =>
  str
    ? str
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    : str

const scoreColor = score => {
  if (score == null) return '#d9d9d9'
  if (score >= 85) return '#52c41a'
  if (score >= 60) return '#faad14'
  return '#ff4d4f'
}

const InfoRow = ({ label, children }) => (
  <div style={INFO_ROW_STYLE}>
    <div style={INFO_ROW_LABEL_STYLE}>{label}:</div>
    <div style={INFO_ROW_VALUE_STYLE}>
      {children == null || children === '' || children === false
        ? null
        : children}
    </div>
  </div>
)

const ROLE_SECTIONS = [
  { key: ASSIGNEE_TYPE.LEADER, label: 'Người chịu trách nhiệm' },
  { key: ASSIGNEE_TYPE.HANDLER, label: 'Người xử lý' },
  { key: ASSIGNEE_TYPE.FOLLOWER, label: 'Người theo dõi' },
]

const EditActionButtons = ({ saving, onCancel, onSave }) => (
  <div style={FLEX_ROW_GAP6}>
    <Button size="small" onClick={onCancel}>
      Hủy bỏ
    </Button>
    <Button size="small" type="primary" loading={saving} onClick={onSave}>
      Lưu
    </Button>
  </div>
)

const SubKpiResultRow = ({ subKpi }) => {
  const handlers = (subKpi.assignees || []).filter(
    a => a.permission === ASSIGNEE_TYPE.HANDLER
  )
  const handlerNames = handlers
    .map(h => toTitleCase(h.name_uppercase || h.assignee_code))
    .join(', ')
  const name = subKpi.kpi || subKpi.purpose || '(Chưa có tên)'

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '7px 4px',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        gap: 8,
        fontSize: 13,
      }}>
      <div style={{ flex: 1, fontWeight: 500, color: '#1a1a1a', minWidth: 0 }}>
        <Tooltip title={name} placement="topLeft">
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {name}
          </span>
        </Tooltip>
      </div>
      <div
        style={{
          width: 140,
          color: '#000',
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        <Tooltip title={handlerNames || ''}>
          {handlerNames || <span style={{ color: '#aaa' }}>–</span>}
        </Tooltip>
      </div>
      <div style={{ width: 56, textAlign: 'center', flexShrink: 0 }}>
        {subKpi.score != null ? (
          <Tag
            color={scoreColor(subKpi.score)}
            style={{ minWidth: 36, textAlign: 'center', fontWeight: 600 }}>
            {subKpi.score}
          </Tag>
        ) : (
          <span style={{ color: '#aaa' }}>–</span>
        )}
      </div>
      <div
        style={{
          width: 160,
          textAlign: 'center',
          flexShrink: 0,
          color: '#000',
          fontSize: 12,
        }}>
        {subKpi.report_date ? (
          subKpi.report_end_date ? (
            `${moment(subKpi.report_date).format('DD/MM/YYYY')} - ${moment(
              subKpi.report_end_date
            ).format('DD/MM/YYYY')}`
          ) : (
            moment(subKpi.report_date).format('DD/MM/YYYY')
          )
        ) : (
          <span style={{ color: '#aaa' }}>–</span>
        )}
      </div>
    </div>
  )
}

const KpiV2DetailExpand = ({
  kpiId,
  onDeleted,
  onUpdated,
  aspects,
  selectPeopleStore,
  fileStore,
  authenticationStore,
}) => {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)

  const [isEditingInfo, setIsEditingInfo] = useState(false)
  const [isEditingAssignee, setIsEditingAssignee] = useState(false)
  const [isEditingFiles, setIsEditingFiles] = useState(false)
  const [isEditingResult, setIsEditingResult] = useState(false)
  const [infoSaving, setInfoSaving] = useState(false)
  const [assigneeSaving, setAssigneeSaving] = useState(false)
  const [fileSaving, setFileSaving] = useState(false)
  const [resultSaving, setResultSaving] = useState(false)

  const [infoForm] = Form.useForm()
  const [resultForm] = Form.useForm()
  const [fileList, setFileList] = useState([])

  // ── SubKPI state ──────────────────────────────────────────────────────────
  const [showCreateSubKpi, setShowCreateSubKpi] = useState(false)
  const [subKpiDetails, setSubKpiDetails] = useState([])
  const [loadingSubKpi, setLoadingSubKpi] = useState(false)

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
  } = selectPeopleStore

  const fetchDetail = useCallback(async () => {
    if (!kpiId) return
    setLoading(true)
    try {
      const res = await KpiV2Request.getById(kpiId)
      setDetail(res.data)
    } catch {
      message.error('Không thể tải chi tiết KPI.')
    } finally {
      setLoading(false)
    }
  }, [kpiId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  // Fetch sub KPI phụ by parent kpi_id — accessible to all KPI assignees
  const fetchSubKpiDetails = useCallback(async () => {
    if (!kpiId) return
    setLoadingSubKpi(true)
    try {
      const res = await KpiV2Request.getAllSubKpi({ kpi_id: kpiId, size: 200 })
      const list = res.data?.content ?? res.data ?? []
      // Fetch full detail (with assignees) for each
      const results = await Promise.all(
        list.map(s => KpiV2Request.getSubKpiById(s.id).catch(() => null))
      )
      setSubKpiDetails(
        results
          .filter(Boolean)
          .map(r => r.data)
          .filter(Boolean)
      )
    } catch {
      // silent
    } finally {
      setLoadingSubKpi(false)
    }
  }, [kpiId])

  useEffect(() => {
    if (detail) {
      fetchSubKpiDetails()
    }
  }, [detail, fetchSubKpiDetails])

  // ── Info edit ──────────────────────────────────────────────────────────────

  const handleStartEditInfo = () => {
    infoForm.setFieldsValue({
      ...detail,
      start_date: detail.start_date ? moment(detail.start_date) : null,
      end_date: detail.end_date ? moment(detail.end_date) : null,
    })
    setIsEditingInfo(true)
  }

  const handleSaveInfo = async () => {
    try {
      const values = await infoForm.validateFields()
      setInfoSaving(true)
      await KpiV2Request.update(kpiId, {
        ...detail,
        attachment: (detail.attachment || []).map(f => f.file_id || f.id),
        ...values,
        start_date: values.start_date
          ? values.start_date.format('YYYY-MM-DD')
          : undefined,
        end_date: values.end_date
          ? values.end_date.format('YYYY-MM-DD')
          : undefined,
      })
      message.success('Cập nhật thông tin KPI thành công!')
      setIsEditingInfo(false)
      fetchDetail()
      onUpdated?.()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setInfoSaving(false)
    }
  }

  // ── Assignee edit ──────────────────────────────────────────────────────────

  const handleStartEditAssignee = () => {
    selectPeopleStore.clearSelectPeopleStore()
    selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
    selectPeopleStore.getDepartmentWithUsers()
    if (detail.assignees) {
      const newUserData = { ...selectPeopleStore.selectUserData }
      const newDeptData = { ...selectPeopleStore.selectDepartmentData }
      const newGroupData = { ...selectPeopleStore.selectGroupData }
      ROLE_SECTIONS.forEach(({ key }) => {
        const members = detail.assignees
          .filter(a => a.permission === key)
          .map(a => ({
            id: a.assignee_code,
            name: a.name_uppercase,
            full_name: a.name_uppercase,
            image_id: a.image,
            assignee_type: a.assignee_type,
          }))
        newUserData[key] = members.filter(
          a => a.assignee_type === ASSIGNEE_TYPE.USER
        )
        newDeptData[key] = members.filter(
          a => a.assignee_type === ASSIGNEE_TYPE.DEPARTMENT
        )
        newGroupData[key] = members.filter(
          a => a.assignee_type === ASSIGNEE_TYPE.GROUP
        )
      })
      selectPeopleStore.setSelectUserData(newUserData)
      selectPeopleStore.setSelectDepartmentData(newDeptData)
      selectPeopleStore.setSelectGroupData(newGroupData)
    }
    setIsEditingAssignee(true)
  }

  const getListByKey = key => [
    ...(selectUserData[key] || []),
    ...(selectDepartmentData[key] || []),
    ...(selectGroupData[key] || []),
  ]

  const handleSaveAssignees = async () => {
    try {
      setAssigneeSaving(true)
      const assignees = ROLE_SECTIONS.flatMap(({ key }) =>
        getListByKey(key).map(el => ({
          assignee_code: el.id,
          assignee_type: el.assignee_type || ASSIGNEE_TYPE.USER,
          permission: key,
        }))
      )
      await KpiV2Request.update(kpiId, {
        ...detail,
        attachment: (detail.attachment || []).map(f => f.file_id || f.id),
        assignees,
      })
      message.success('Cập nhật người tham gia thành công!')
      setIsEditingAssignee(false)
      selectPeopleStore.clearSelectPeopleStore()
      selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
      selectPeopleStore.getDepartmentWithUsers()
      fetchDetail()
    } catch (error) {
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setAssigneeSaving(false)
    }
  }

  // ── File edit ──────────────────────────────────────────────────────────────

  const handleStartEditFiles = () => {
    setFileList(
      (detail.attachment || []).map(f => ({
        id: f.file_id || f.id,
        name: f.file_name || f.name,
        file_type: utils.getExtensionFile(f.file_name || f.name || ''),
      }))
    )
    setIsEditingFiles(true)
  }

  const handleFileUpload = async options => {
    const { file, onSuccess: onFileSuccess, onError: onFileError } = options
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fileStore.uploadFile(formData)
      const data = res.data
      setFileList(prev => [
        ...prev,
        {
          id: data.file_id || data.id,
          name: data.file_name || data.name,
          file_type: utils.getExtensionFile(data.file_name || data.name || ''),
        },
      ])
      onFileSuccess(res.data, file)
    } catch {
      message.error('Tải lên tệp tin thất bại.')
      onFileError(new Error('upload failed'))
    }
  }

  const handleSaveFiles = async () => {
    try {
      setFileSaving(true)
      await KpiV2Request.update(kpiId, {
        ...detail,
        attachment: fileList.map(f => f.id),
      })
      message.success('Cập nhật tài liệu thành công!')
      setIsEditingFiles(false)
      fetchDetail()
    } catch (error) {
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setFileSaving(false)
    }
  }

  const handleDeleteFile = async fileId => {
    try {
      const remaining = (detail.attachment || [])
        .filter(f => (f.file_id || f.id) !== fileId)
        .map(f => f.file_id || f.id)
      await KpiV2Request.update(kpiId, { ...detail, attachment: remaining })
      message.success('Đã xóa tài liệu!')
      fetchDetail()
    } catch {
      message.error('Xóa tài liệu thất bại.')
    }
  }

  // ── Result edit ────────────────────────────────────────────────────────────

  const handleStartEditResult = () => {
    resultForm.setFieldsValue({
      result: detail.result,
      score: detail.score,
      result_description: detail.result_description,
    })
    setIsEditingResult(true)
  }

  const handleSaveResult = async () => {
    try {
      const values = await resultForm.validateFields()
      setResultSaving(true)
      await KpiV2Request.update(kpiId, {
        ...detail,
        attachment: (detail.attachment || []).map(f => f.file_id || f.id),
        result: values.result ?? null,
        score: values.score,
        result_description: values.result_description,
      })
      message.success('Cập nhật kết quả thành công!')
      setIsEditingResult(false)
      fetchDetail()
      onUpdated?.()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setResultSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await KpiV2Request.delete(kpiId)
      message.success('Xóa KPI thành công!')
      onDeleted?.()
    } catch {
      message.error('Xóa KPI thất bại.')
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getAspectName = aspectId => {
    const asp = (aspects || []).find(a => a.id === aspectId)
    return asp ? asp.context : aspectId || ''
  }

  const formatEvaluation = v => {
    if (!v) return null
    return String(v)
      .split('\n')
      .map((line, i) => <div key={i}>{line}</div>)
  }

  const renderDateRange = () => {
    const s = detail.start_date
      ? moment(detail.start_date).format(DATE_FORMAT_DEFAULT)
      : null
    const e = detail.end_date
      ? moment(detail.end_date).format(DATE_FORMAT_DEFAULT)
      : null
    return (
      <span style={{ display: 'inline-flex', gap: 16, flexWrap: 'wrap' }}>
        <span>
          <CalendarOutlined style={{ color: blue, marginRight: 4 }} />
          <span style={{ marginRight: 4 }}>Ngày bắt đầu:</span>
          {s || <i style={{ color: '#bbb' }}>Không rõ</i>}
        </span>
        <span>
          <CalendarOutlined style={{ color: blue, marginRight: 4 }} />
          <span style={{ marginRight: 4 }}>Hạn hoàn thành:</span>
          {e || <i style={{ color: '#bbb' }}>Không rõ</i>}
        </span>
      </span>
    )
  }

  const renderUserTagList = (list, permission) =>
    list.map(el => (
      <Tag
        key={el.id}
        className={'tag-selected'}
        color="blue"
        closable
        onClose={() =>
          selectPeopleStore.handleRemoveSelect({ ...el, permission })
        }>
        <AvatarHasName
          imgId={el.image_id}
          size={22}
          name={el.full_name || el.name}
          icon={
            (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
              <BankOutlined />
            )) ||
            (el.assignee_type === ASSIGNEE_TYPE.GROUP && <TeamOutlined />) || (
              <UserOutlined />
            )
          }
        />
      </Tag>
    ))

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid #e8edf3',
          borderRadius: 6,
          padding: '16px 20px',
        }}
        onClick={e => e.stopPropagation()}>
        <Spin spinning={loading}>
          {detail && (
            <Row gutter={[16, 16]}>
              {/* ── Left: Info card ── */}
              <Col xs={24} lg={16}>
                <ContentBlockWrapper style={CARD_STYLE}>
                  <div style={SECTION_TITLE_STYLE}>
                    <span>Thông tin chung</span>
                    {isEditingInfo ? (
                      <EditActionButtons
                        saving={infoSaving}
                        onCancel={() => setIsEditingInfo(false)}
                        onSave={handleSaveInfo}
                      />
                    ) : (
                      <div style={FLEX_ROW_GAP6}>
                        <AccessControlAction
                          aclActionType={ACL_ACTION_TYPE.edit__KPI}>
                          <Button
                            size="small"
                            type="primary"
                            onClick={handleStartEditInfo}>
                            Chỉnh sửa
                          </Button>
                        </AccessControlAction>
                        <AccessControlAction
                          aclActionType={ACL_ACTION_TYPE.delete__KPI}>
                          <Popconfirm
                            title="Bạn có chắc chắn muốn xóa KPI này không?"
                            onConfirm={handleDelete}
                            okText="Xóa"
                            cancelText="Hủy">
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}>
                              Xóa
                            </Button>
                          </Popconfirm>
                        </AccessControlAction>
                      </div>
                    )}
                  </div>

                  {isEditingInfo ? (
                    <Form
                      form={infoForm}
                      layout="horizontal"
                      labelCol={{ flex: '0 0 160px' }}
                      wrapperCol={{ flex: 1 }}
                      labelAlign="left"
                      colon={false}>
                      <Form.Item
                        name="aspect_id"
                        label="Khía cạnh:"
                        style={FORM_ITEM_STYLE}
                        rules={[
                          {
                            required: true,
                            message: 'Vui lòng chọn khía cạnh',
                          },
                        ]}>
                        <Select placeholder="Chọn khía cạnh" allowClear>
                          {(aspects || []).map(asp => (
                            <Option key={asp.id} value={asp.id}>
                              {asp.context}
                              {asp.weight != null ? ` (${asp.weight}%)` : ''}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="purpose"
                        label="Mục tiêu:"
                        style={FORM_ITEM_STYLE}>
                        <Input placeholder="Nhập nội dung" />
                      </Form.Item>
                      <Form.Item
                        name="kgi"
                        label="KGI:"
                        style={FORM_ITEM_STYLE}>
                        <Input placeholder="Nhập nội dung" />
                      </Form.Item>
                      <Form.Item
                        name="csf"
                        label="CSF:"
                        style={FORM_ITEM_STYLE}>
                        <Input placeholder="Nhập nội dung" />
                      </Form.Item>
                      <Form.Item
                        name="kpi"
                        label="KPI:"
                        style={FORM_ITEM_STYLE}>
                        <Input placeholder="Nhập nội dung" />
                      </Form.Item>
                      <Form.Item
                        name="measurement"
                        label="Đơn vị tính:"
                        style={FORM_ITEM_STYLE}>
                        <Input placeholder="VD: %, VNĐ..." />
                      </Form.Item>
                      <Form.Item
                        name="indicator_weight"
                        label="Trọng số chỉ tiêu:"
                        style={FORM_ITEM_STYLE}>
                        <InputNumber
                          min={0}
                          max={100}
                          placeholder="0-100"
                          addonAfter="%"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="evaluation_rule"
                        label="Quy ước đánh giá:"
                        style={FORM_ITEM_STYLE}>
                        <TextArea
                          rows={3}
                          placeholder="Nhập quy ước đánh giá..."
                        />
                      </Form.Item>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="start_date"
                            label="Ngày bắt đầu:"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            style={FORM_ITEM_STYLE}>
                            <DatePicker
                              format="DD/MM/YYYY"
                              style={{ width: '100%' }}
                              suffixIcon={<CalendarOutlined />}
                              placeholder="dd/mm/yyyy"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="end_date"
                            label="Hạn hoàn thành:"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            style={FORM_ITEM_STYLE}>
                            <DatePicker
                              format="DD/MM/YYYY"
                              style={{ width: '100%' }}
                              suffixIcon={<CalendarOutlined />}
                              placeholder="dd/mm/yyyy"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    <>
                      <InfoRow label="Thời gian">{renderDateRange()}</InfoRow>
                      <InfoRow label="Khía cạnh">
                        {getAspectName(detail.aspect_id)}
                      </InfoRow>
                      <InfoRow label="Mục tiêu">{detail.purpose}</InfoRow>
                      <InfoRow label="KGI">{detail.kgi}</InfoRow>
                      <InfoRow label="CSF">{detail.csf}</InfoRow>
                      <InfoRow label="KPI">{detail.kpi}</InfoRow>
                      <InfoRow label="Đơn vị tính">
                        {detail.measurement}
                      </InfoRow>
                      <InfoRow label="Trọng số chỉ tiêu">
                        {detail.indicator_weight != null
                          ? `${detail.indicator_weight}%`
                          : null}
                      </InfoRow>
                      <InfoRow label="Trọng số chung">
                        {detail.overall_weight != null
                          ? `${detail.overall_weight}%`
                          : null}
                      </InfoRow>
                      <InfoRow label="Quy ước đánh giá">
                        {formatEvaluation(detail.evaluation_rule)}
                      </InfoRow>
                    </>
                  )}
                </ContentBlockWrapper>

                {/* Kết quả KPI phụ */}
                <ContentBlockWrapper style={{ ...CARD_STYLE, marginTop: 16 }}>
                  <div style={SECTION_TITLE_STYLE}>
                    <span>Kết quả KPI phụ</span>
                    <AccessControlAction
                      aclActionType={ACL_ACTION_TYPE.edit__KPI}>
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateSubKpi(true)}>
                        Tạo mới
                      </Button>
                    </AccessControlAction>
                  </div>

                  {subKpiDetails.length > 0 && (
                    <div style={SUB_LIST_HEADER_STYLE}>
                      <div style={{ flex: 1 }}>KPI (Chỉ tiêu)</div>
                      <div style={{ width: 140 }}>Người thực hiện</div>
                      <div style={{ width: 56, textAlign: 'center' }}>Điểm</div>
                      <div style={{ width: 160, textAlign: 'center' }}>
                        Thời gian báo cáo
                      </div>
                    </div>
                  )}
                  <Spin spinning={loadingSubKpi}>
                    <div>
                      {subKpiDetails.map((sub, idx) => (
                        <SubKpiResultRow key={sub.id || idx} subKpi={sub} />
                      ))}
                    </div>
                  </Spin>
                </ContentBlockWrapper>
              </Col>

              {/* ── Right: side cards ── */}
              <Col xs={24} lg={8}>
                {/* Người tham gia */}
                <ContentBlockWrapper
                  style={{ ...CARD_STYLE, marginBottom: 12 }}>
                  <div style={SECTION_TITLE_STYLE}>
                    <span>Người tham gia</span>
                    {isEditingAssignee ? (
                      <EditActionButtons
                        saving={assigneeSaving}
                        onCancel={() => {
                          setIsEditingAssignee(false)
                          selectPeopleStore.clearSelectPeopleStore()
                        }}
                        onSave={handleSaveAssignees}
                      />
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleStartEditAssignee}>
                        Phân bổ
                      </Button>
                    )}
                  </div>

                  {isEditingAssignee ? (
                    <div style={FLEX_COL_GAP4}>
                      {ROLE_SECTIONS.map(({ key, label }) => (
                        <div key={key}>
                          <SelectTitle>
                            <span style={{ marginRight: 10 }}>{label}:</span>
                            <Space>
                              <Tooltip
                                title={`Chọn ${label.toLowerCase()}`}
                                color={blue}>
                                <Button
                                  icon={
                                    <UserOutlined style={{ color: blue }} />
                                  }
                                  onClick={() =>
                                    handleOpenSelectUserDepartmentPopup(key)
                                  }
                                />
                              </Tooltip>
                            </Space>
                          </SelectTitle>
                          <SelectList
                            style={{ maxHeight: 'none', overflowY: 'visible' }}>
                            {renderUserTagList(getListByKey(key), key)}
                          </SelectList>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={FLEX_COL_GAP4}>
                      {ROLE_SECTIONS.map(({ key, label }) => {
                        const members = (detail.assignees || []).filter(
                          a => a.permission === key
                        )
                        return (
                          <div key={key}>
                            <SelectTitle>
                              <span style={{ color: 'black', fontWeight: 700 }}>
                                {label}:
                              </span>
                            </SelectTitle>
                            <SelectList
                              style={{
                                maxHeight: 'none',
                                overflowY: 'visible',
                              }}>
                              {members.length > 0 && (
                                <PopoverWatchTime
                                  userList={members.map(a => ({
                                    id: a.assignee_code,
                                    full_name: a.name_uppercase,
                                    assignee_type: a.assignee_type,
                                    permission: a.permission,
                                  }))}
                                  title={label}
                                  isFullViewer={true}
                                  typeId={kpiId}
                                  compared_date={
                                    VIEWLOG_DATE.internal_document_until
                                  }
                                  created_at={detail.created_at || ''}
                                />
                              )}
                            </SelectList>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </ContentBlockWrapper>

                {/* Tài liệu đính kèm */}
                <ContentBlockWrapper
                  style={{ ...CARD_STYLE, marginBottom: 12 }}>
                  <div style={SECTION_TITLE_STYLE}>
                    <span>Tài liệu đính kèm</span>
                    {isEditingFiles ? (
                      <EditActionButtons
                        saving={fileSaving}
                        onCancel={() => setIsEditingFiles(false)}
                        onSave={handleSaveFiles}
                      />
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleStartEditFiles}>
                        + Thêm tài liệu
                      </Button>
                    )}
                  </div>

                  {isEditingFiles ? (
                    <>
                      <Upload
                        customRequest={handleFileUpload}
                        showUploadList={false}>
                        <Button
                          icon={<CloudUploadOutlined />}
                          style={{ marginBottom: 8 }}>
                          Chọn tệp
                        </Button>
                      </Upload>
                      <div style={FLEX_COL_GAP4}>
                        {fileList.map(f => (
                          <UploadFileItem
                            key={f.id}
                            file_id={f.id}
                            file_name={f.name}
                            file_type={f.file_type}
                            handleRemoveFileFromUploadList={() =>
                              setFileList(prev =>
                                prev.filter(x => x.id !== f.id)
                              )
                            }
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={FILE_LIST_STYLE}>
                      {(detail.attachment || []).map(f => (
                        <FileItem
                          key={f.file_id}
                          file_id={f.file_id}
                          file_name={f.file_name}
                          file_type={utils.getExtensionFile(f.file_name || '')}
                          onDelete={() => handleDeleteFile(f.file_id || f.id)}
                        />
                      ))}
                    </div>
                  )}
                </ContentBlockWrapper>

                {/* Kết quả & Đánh giá */}
                <ContentBlockWrapper style={CARD_STYLE}>
                  <div style={SECTION_TITLE_STYLE}>
                    <span>Kết quả & Đánh giá</span>
                    {isEditingResult ? (
                      <EditActionButtons
                        saving={resultSaving}
                        onCancel={() => setIsEditingResult(false)}
                        onSave={handleSaveResult}
                      />
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleStartEditResult}>
                        Cập nhật kết quả
                      </Button>
                    )}
                  </div>

                  {isEditingResult ? (
                    <Form form={resultForm} layout="vertical" colon={false}>
                      <Form.Item
                        name="result"
                        label="Kết quả thực hiện"
                        style={FORM_ITEM_STYLE}>
                        <Input placeholder="Nhập kết quả thực hiện..." />
                      </Form.Item>
                      <Form.Item
                        name="score"
                        label="Điểm:"
                        style={FORM_ITEM_STYLE}>
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: '100%' }}
                          placeholder="0 - 100"
                        />
                      </Form.Item>
                      <Form.Item
                        name="result_description"
                        label="Thuyết minh kết quả:"
                        style={FORM_ITEM_STYLE}>
                        <TextArea rows={3} placeholder="Nhập thuyết minh..." />
                      </Form.Item>
                    </Form>
                  ) : (
                    <>
                      <InfoRow label="Kết quả thực hiện: ">
                        {detail.result}
                      </InfoRow>
                      <InfoRow label="Điểm">{detail.score}</InfoRow>
                      <InfoRow label="Thuyết minh">
                        {detail.result_description}
                      </InfoRow>
                    </>
                  )}
                </ContentBlockWrapper>
              </Col>
            </Row>
          )}
        </Spin>
      </div>

      <SelectPeoplePopup
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />

      <KpiV2SubUpsertModal
        visible={showCreateSubKpi}
        onClose={() => setShowCreateSubKpi(false)}
        onSuccess={() => {
          fetchDetail()
          fetchSubKpiDetails()
        }}
        aspects={aspects}
        parentKpi={detail}
      />
    </>
  )
}

export default inject(
  'selectPeopleStore',
  'fileStore',
  'authenticationStore'
)(observer(KpiV2DetailExpand))
